import type React from 'react';

import { Box, FormField, FormHelp, Label, Textarea } from '@zillow/constellation-10';

import type { Section } from '../../../models/index';
import { PROPERTY_ADDRESS } from '../../../constants/index';
import { isMultiFamily } from './Utilities/MultiFamilyLandlordUtility';
import { FULLSTORY_EXCLUDE } from '../../../constants/analytics';

type Props = {
  section: Section;
  setSectionValues: (keyValuePairs: object) => void;
};

export default function PropertyAddress_v1({ section, setSectionValues }: Props): JSX.Element {
  function onPropertyAddressChange(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    const { value } = event.currentTarget;

    setSectionValues({
      [PROPERTY_ADDRESS.FIELD_NAME.PROPERTY_ADDRESS]: value,
    });
  }

  function onDevelopmentAddressChange(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void {
    const { value } = event.currentTarget;

    setSectionValues({
      [PROPERTY_ADDRESS.FIELD_NAME.DEVELOPMENT_ADDRESS]: value,
    });
  }

  const isMultiFamilyLandlord = isMultiFamily(section);
  const textareaHeight = isMultiFamilyLandlord ? '7.125rem' : '5.625rem';

  return (
    <Box css={{ display: 'flex', flexDirection: 'column', gap: 'layout.tighter' }}>
      <FormField
        required
        className={FULLSTORY_EXCLUDE}
        error={section.hasFieldError(PROPERTY_ADDRESS.FIELD_NAME.PROPERTY_ADDRESS)}
        label={<Label id="property-address">What is the property’s address?</Label>}
        control={
          <Textarea
            aria-labelledby="property-address"
            value={section.getFieldValue(PROPERTY_ADDRESS.FIELD_NAME.PROPERTY_ADDRESS)}
            css={{ resize: 'vertical', height: textareaHeight, '&:focus': { height: textareaHeight } }}
            onChange={onPropertyAddressChange}
          />
        }
        description={
          section.hasFieldError(PROPERTY_ADDRESS.FIELD_NAME.PROPERTY_ADDRESS) && (
            <FormHelp>{PROPERTY_ADDRESS.ERROR_MESSAGE.PROPERTY_ADDRESS}</FormHelp>
          )
        }
      />

      {isMultiFamilyLandlord && (
        <FormField
          required
          className={FULLSTORY_EXCLUDE}
          error={section.hasFieldError(PROPERTY_ADDRESS.FIELD_NAME.DEVELOPMENT_ADDRESS)}
          label={
            <Label id="development-address">
              Development address (if same as above, enter the property address without the Unit Number)
            </Label>
          }
          control={
            <Textarea
              aria-labelledby="development-address"
              css={{ resize: 'vertical', height: '7.125rem', '&:focus': { height: '7.125rem' } }}
              value={section.getFieldValue(PROPERTY_ADDRESS.FIELD_NAME.DEVELOPMENT_ADDRESS)}
              onChange={onDevelopmentAddressChange}
            />
          }
          description={
            section.hasFieldError(PROPERTY_ADDRESS.FIELD_NAME.DEVELOPMENT_ADDRESS) && (
              <FormHelp>{PROPERTY_ADDRESS.ERROR_MESSAGE.DEVELOPMENT_ADDRESS}</FormHelp>
            )
          }
        />
      )}
    </Box>
  );
}
